/**
 * Structured cron-expression builder.
 *
 * Operators were complaining that raw 5-field cron strings are
 * unreadable. This component renders a friendly UI in three flavors:
 *
 *   - daily   "every day at HH:MM"
 *   - weekly  "every {Mon,Tue,...} at HH:MM"
 *   - hourly  "every N hours at :MM"
 *   - custom  raw cron expression (for power users)
 *
 * Time inputs are interpreted in the controller's UTC frame (matching
 * the existing field-hint), with a helper line showing the equivalent
 * in the operator's local timezone for sanity-checking.
 */
import { useEffect, useMemo, useState } from "react";

export interface CronBuilderProps {
  value: string;
  onChange: (next: string) => void;
}

type Mode = "daily" | "weekly" | "hourly" | "custom";

interface DailyParts {
  hour: number;
  minute: number;
}
interface WeeklyParts extends DailyParts {
  /** 0 = Sun ... 6 = Sat (cron convention with 0=Sun). */
  days: number[];
}
interface HourlyParts {
  everyHours: number;
  minute: number;
}

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function CronBuilder({ value, onChange }: CronBuilderProps) {
  const [mode, setMode] = useState<Mode>(() => detectMode(value));
  const [daily, setDaily] = useState<DailyParts>(() => parseDaily(value));
  const [weekly, setWeekly] = useState<WeeklyParts>(() => parseWeekly(value));
  const [hourly, setHourly] = useState<HourlyParts>(() => parseHourly(value));
  const [custom, setCustom] = useState(value);

  // Re-sync when the parent passes a different value (e.g. switching
  // between schedule rows).
  useEffect(() => {
    const m = detectMode(value);
    setMode(m);
    setDaily(parseDaily(value));
    setWeekly(parseWeekly(value));
    setHourly(parseHourly(value));
    setCustom(value);
  }, [value]);

  // Recompute the cron expression when the active mode's fields change.
  useEffect(() => {
    let next: string;
    switch (mode) {
      case "daily":
        next = `${daily.minute} ${daily.hour} * * *`;
        break;
      case "weekly":
        next = weekly.days.length === 0
          ? `${weekly.minute} ${weekly.hour} * * *`
          : `${weekly.minute} ${weekly.hour} * * ${[...weekly.days].sort().join(",")}`;
        break;
      case "hourly":
        next = `${hourly.minute} */${hourly.everyHours} * * *`;
        break;
      case "custom":
      default:
        next = custom.trim();
        break;
    }
    if (next !== value && next.length > 0) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, daily, weekly, hourly, custom]);

  const summary = useMemo(() => describeCron(value), [value]);
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className="cron">
      <div className="cron__modes" role="tablist">
        {(["daily", "weekly", "hourly", "custom"] as Mode[]).map((m) => (
          <button
            type="button"
            key={m}
            role="tab"
            aria-selected={mode === m}
            className={`cron__mode${mode === m ? " is-active" : ""}`}
            onClick={() => setMode(m)}
          >
            {m}
          </button>
        ))}
      </div>

      {mode === "daily" && (
        <div className="cron__row">
          <span className="cron__label">at</span>
          <TimeInput
            hour={daily.hour}
            minute={daily.minute}
            onChange={(h, m) => setDaily({ hour: h, minute: m })}
          />
          <span className="cron__hint">UTC</span>
        </div>
      )}

      {mode === "weekly" && (
        <>
          <div className="cron__row cron__row--days">
            {DAY_LABELS.map((lbl, idx) => {
              const active = weekly.days.includes(idx);
              return (
                <button
                  type="button"
                  key={lbl}
                  className={`cron__day${active ? " is-active" : ""}`}
                  onClick={() => {
                    setWeekly((prev) => ({
                      ...prev,
                      days: active
                        ? prev.days.filter((d) => d !== idx)
                        : [...prev.days, idx],
                    }));
                  }}
                >
                  {lbl}
                </button>
              );
            })}
          </div>
          <div className="cron__row">
            <span className="cron__label">at</span>
            <TimeInput
              hour={weekly.hour}
              minute={weekly.minute}
              onChange={(h, m) => setWeekly((prev) => ({ ...prev, hour: h, minute: m }))}
            />
            <span className="cron__hint">UTC</span>
          </div>
        </>
      )}

      {mode === "hourly" && (
        <div className="cron__row">
          <span className="cron__label">every</span>
          <select
            value={hourly.everyHours}
            onChange={(e) =>
              setHourly((prev) => ({ ...prev, everyHours: Number(e.target.value) }))
            }
          >
            {[1, 2, 3, 4, 6, 8, 12].map((h) => (
              <option key={h} value={h}>
                {h} h
              </option>
            ))}
          </select>
          <span className="cron__label">at minute</span>
          <input
            type="number"
            min={0}
            max={59}
            value={hourly.minute}
            onChange={(e) =>
              setHourly((prev) => ({
                ...prev,
                minute: clamp(Number(e.target.value), 0, 59),
              }))
            }
            style={{ width: 64 }}
          />
        </div>
      )}

      {mode === "custom" && (
        <div className="cron__row">
          <input
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="0 9 * * MON-FRI"
            style={{ flex: 1, fontFamily: "var(--font-mono)" }}
          />
        </div>
      )}

      <div className="cron__footer">
        <div className="cron__summary">{summary}</div>
        <div className="cron__expr">
          <span className="muted">cron</span> <code>{value || "—"}</code>
        </div>
        <div className="cron__tz">
          times are <strong>UTC</strong> · viewer tz: <code>{localTz}</code>
        </div>
      </div>
    </div>
  );
}

function TimeInput({
  hour,
  minute,
  onChange,
}: {
  hour: number;
  minute: number;
  onChange: (hour: number, minute: number) => void;
}) {
  return (
    <input
      type="time"
      value={`${pad(hour)}:${pad(minute)}`}
      onChange={(e) => {
        const [hStr, mStr] = e.target.value.split(":");
        if (hStr === undefined || mStr === undefined) return;
        const h = clamp(Number(hStr), 0, 23);
        const m = clamp(Number(mStr), 0, 59);
        onChange(h, m);
      }}
      style={{ width: 110 }}
    />
  );
}

// ---- parsing -------------------------------------------------------------

function detectMode(expr: string): Mode {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return "custom";
  const [min, hour, dom, mon, dow] = parts;
  const everyDom = dom === "*";
  const everyMon = mon === "*";
  const numeric = /^\d+$/;
  if (everyDom && everyMon) {
    if (dow === "*" && numeric.test(min) && numeric.test(hour)) return "daily";
    if (numeric.test(min) && /^\*\/\d+$/.test(hour)) return "hourly";
    if (numeric.test(min) && numeric.test(hour) && /^[\dA-Z,\-]+$/.test(dow))
      return "weekly";
  }
  return "custom";
}

function parseDaily(expr: string): DailyParts {
  const parts = expr.trim().split(/\s+/);
  if (parts.length === 5 && /^\d+$/.test(parts[0]) && /^\d+$/.test(parts[1])) {
    return { minute: clamp(Number(parts[0]), 0, 59), hour: clamp(Number(parts[1]), 0, 23) };
  }
  return { minute: 0, hour: 9 };
}

function parseWeekly(expr: string): WeeklyParts {
  const parts = expr.trim().split(/\s+/);
  const base = parseDaily(expr);
  if (parts.length !== 5) return { ...base, days: [1, 2, 3, 4, 5] };
  const dow = parts[4];
  if (dow === "*") return { ...base, days: [0, 1, 2, 3, 4, 5, 6] };
  const days = expandDow(dow);
  return { ...base, days };
}

function parseHourly(expr: string): HourlyParts {
  const parts = expr.trim().split(/\s+/);
  if (parts.length === 5) {
    const minute = /^\d+$/.test(parts[0]) ? clamp(Number(parts[0]), 0, 59) : 0;
    const m = /^\*\/(\d+)$/.exec(parts[1]);
    if (m) {
      return { minute, everyHours: clamp(Number(m[1]), 1, 23) };
    }
  }
  return { minute: 0, everyHours: 1 };
}

const DOW_NAMES: Record<string, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

function expandDow(spec: string): number[] {
  const out = new Set<number>();
  for (const piece of spec.split(",")) {
    const range = piece.split("-");
    if (range.length === 2) {
      const a = toDow(range[0]);
      const b = toDow(range[1]);
      if (a == null || b == null) continue;
      for (let d = a; d !== (b + 1) % 7; d = (d + 1) % 7) {
        out.add(d);
        if (out.size > 7) break;
      }
    } else {
      const d = toDow(piece);
      if (d != null) out.add(d);
    }
  }
  return [...out].sort();
}

function toDow(token: string): number | null {
  const t = token.trim().toUpperCase();
  if (/^\d+$/.test(t)) return clamp(Number(t), 0, 6);
  if (t in DOW_NAMES) return DOW_NAMES[t];
  return null;
}

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

function clamp(n: number, lo: number, hi: number): number {
  if (Number.isNaN(n)) return lo;
  return Math.min(hi, Math.max(lo, n));
}

// ---- human-readable summary ---------------------------------------------

export function describeCron(expr: string): string {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return "custom schedule";
  const [min, hour, dom, mon, dow] = parts;
  const at = `${pad(Number(hour) || 0)}:${pad(Number(min) || 0)}`;
  if (dom === "*" && mon === "*") {
    const hourEvery = /^\*\/(\d+)$/.exec(hour);
    if (hourEvery) {
      return `every ${hourEvery[1]}h at :${pad(Number(min) || 0)} UTC`;
    }
    if (dow === "*") return `every day at ${at} UTC`;
    if (/^[\dA-Z,\-]+$/.test(dow)) {
      const days = expandDow(dow);
      if (days.length === 7) return `every day at ${at} UTC`;
      const labels = days.map((d) => DAY_LABELS[d]);
      return `every ${labels.join(", ")} at ${at} UTC`;
    }
  }
  return "custom schedule";
}
